import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Radio from '@mui/material/Radio';
import LaptopMacIcon from '@mui/icons-material/LaptopMac';
import PublicIcon from '@mui/icons-material/Public';
import Modal from '../modal/modal';
import './sign-out-modal.scss';

/**
 * SignOutModal Component
 *
 * A confirmation dialog with radio selection defaulting to web logout.
 * Allows users to choose between logging out of the current web session
 * or invalidating all active sessions across all devices.
 *
 * @param {object} props - Component properties
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onLogout - Callback to execute logout with boolean allDevices flag
 * @returns {React.ReactElement} Rendered sign-out modal
 */
export default function SignOutModal({ open, onClose, onLogout }) {
  const [selectedOption, setSelectedOption] = useState('web');

  useEffect(() => {
    if (open) {
      setSelectedOption('web');
    }
  }, [open]);

  /**
   * Confirms logout selection and triggers logout callback.
   *
   * @returns {void}
   */
  const handleConfirmLogout = () => {
    onLogout(selectedOption === 'all');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      modalHeading="Sign Out"
      showActionBtn
      modalSaveTxt="Log Out"
      onClick={handleConfirmLogout}
      modalCloseTxt="Cancel"
      modalCloseBtn={onClose}
      className="signout-modal-dialog"
      dataCy="signout-modal"
    >
      <div className="signout-modal-content">
        <p className="signout-subtitle">
          Choose how you would like to sign out of your account:
        </p>

        <div
          role="button"
          tabIndex={0}
          className={`signout-option-button ${selectedOption === 'web' ? 'selected' : ''}`}
          onClick={() => setSelectedOption('web')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedOption('web'); }}
          data-cy="signout-web"
        >
          <div className="signout-icon-container web-device">
            <LaptopMacIcon />
          </div>
          <div className="signout-text-container">
            <h4 className="signout-option-title">Logout (Web)</h4>
            <p className="signout-option-desc">Keep your mobile session active</p>
          </div>
          <Radio
            checked={selectedOption === 'web'}
            onChange={() => setSelectedOption('web')}
            value="web"
            name="signout-option"
            color="primary"
            size="small"
          />
        </div>

        <div
          role="button"
          tabIndex={0}
          className={`signout-option-button ${selectedOption === 'all' ? 'selected all-devices-card' : ''}`}
          onClick={() => setSelectedOption('all')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedOption('all'); }}
          data-cy="signout-all-devices"
        >
          <div className="signout-icon-container all-devices">
            <PublicIcon />
          </div>
          <div className="signout-text-container">
            <h4 className="signout-option-title all-devices-text">Logout All Devices</h4>
            <p className="signout-option-desc">Sign out from all active sessions</p>
          </div>
          <Radio
            checked={selectedOption === 'all'}
            onChange={() => setSelectedOption('all')}
            value="all"
            name="signout-option"
            color="primary"
            size="small"
          />
        </div>
      </div>
    </Modal>
  );
}

SignOutModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
};
